import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { formatPrice, formatDate } from "../utils/format";
import { CheckCircle, XCircle } from "lucide-react";
import toast from "react-hot-toast";

export default function AdminSubmissionsPage() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  async function load() {
    const snap = await getDocs(query(collection(db, "product_submissions"), where("status", "==", "pending")));
    setSubmissions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function approve(sub) {
    // Publish as real product
    await addDoc(collection(db, "products"), {
      name: sub.name,
      category: sub.category,
      description: sub.description,
      price: sub.price,
      originalPrice: sub.originalPrice || null,
      stock: sub.stock || 0,
      brand: sub.brand || "",
      images: sub.images || [],
      specs: sub.specs || {},
      tags: sub.tags || [],
      featured: false,
      status: "active",
      rating: 0,
      reviewCount: 0,
      sellerId: sub.submittedBy,
      sellerEmail: sub.businessEmail,
      businessName: sub.businessName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await updateDoc(doc(db, "product_submissions", sub.id), { status: "approved", approvedAt: serverTimestamp() });
    toast.success("Product approved and published!");
    setSelected(null);
    load();
  }

  async function reject(id) {
    const reason = window.prompt("Reason for rejection (optional):");
    await updateDoc(doc(db, "product_submissions", id), { status: "rejected", rejectedAt: serverTimestamp(), rejectionReason: reason || "" });
    toast.success("Submission rejected");
    setSelected(null);
    load();
  }

  return (
    <div style={{ padding: "clamp(20px, 4vw, 40px) clamp(14px, 3vw, 48px)", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>Product Submissions</h1>
        <p style={{ color: "var(--text-muted)", fontSize: 15, marginTop: 4 }}>Review and approve products submitted by business sellers.</p>
      </div>

      <div className={selected ? "admin-detail-layout" : ""} style={{ display: "grid", gridTemplateColumns: selected ? undefined : "1fr", gap: 24, alignItems: "start", transition: "all 0.3s ease" }}>
        {/* List */}
        <div>
          {loading ? <div style={{ padding: 60, textAlign: "center" }}><div className="loader"><div className="spinner" /></div></div> : submissions.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: 80, color: "var(--text-muted)", background: "#fff", border: "1px dashed var(--border)" }}>
              <CheckCircle size={48} style={{ margin: "0 auto 16px", color: "var(--success)", opacity: 0.8 }} />
              <h3 style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 700, marginBottom: 8, color: "var(--text)" }}>All caught up!</h3>
              <p style={{ fontSize: 15 }}>No pending submissions to review.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {submissions.map(sub => (
                <div key={sub.id} className="card" style={{ padding: 20, display: "flex", gap: 20, alignItems: "center", cursor: "pointer", background: selected?.id === sub.id ? "rgba(232, 71, 26, 0.04)" : "#fff", border: selected?.id === sub.id ? "1px solid var(--brand)" : "1px solid var(--border)", transition: "all 0.2s" }}
                  onClick={() => { setSelected(sub); setActiveImg(0); }}
                  onMouseEnter={e => { if (selected?.id !== sub.id) e.currentTarget.style.borderColor = "#CBD5E1"; }}
                  onMouseLeave={e => { if (selected?.id !== sub.id) e.currentTarget.style.borderColor = "var(--border)"; }}>
                  <div style={{ width: 80, height: 80, borderRadius: 12, overflow: "hidden", background: "var(--surface)", border: "1px solid var(--border)", flexShrink: 0 }}>
                    {sub.images?.[0] ? <img src={sub.images[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>📦</div>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4, color: "var(--text)" }}>{sub.name}</div>
                    <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 8 }}>{sub.category} <span style={{ margin: "0 6px", color: "var(--border)" }}>|</span> <span style={{ fontWeight: 600 }}>{sub.businessName}</span></div>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <span style={{ fontFamily: "var(--font-head)", fontSize: 15, fontWeight: 800, color: "var(--brand)" }}>{formatPrice(sub.price)}</span>
                      <span style={{ fontSize: 12, color: "var(--text-muted)", background: "var(--surface)", padding: "2px 8px", borderRadius: 100 }}>{formatDate(sub.createdAt)}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                    <button onClick={e => { e.stopPropagation(); approve(sub); }} className="btn btn-sm" style={{ background: "rgba(16, 185, 129, 0.1)", color: "#10B981", border: "none", padding: "8px 16px", fontSize: 13, transition: "all 0.2s" }} onMouseEnter={e => { e.currentTarget.style.background = "#10B981"; e.currentTarget.style.color = "#fff"; }} onMouseLeave={e => { e.currentTarget.style.background = "rgba(16, 185, 129, 0.1)"; e.currentTarget.style.color = "#10B981"; }}>
                      <CheckCircle size={15} /> Approve
                    </button>
                    <button onClick={e => { e.stopPropagation(); reject(sub.id); }} className="btn btn-sm" style={{ background: "rgba(239, 68, 68, 0.1)", color: "#EF4444", border: "none", padding: "8px 16px", fontSize: 13, transition: "all 0.2s" }} onMouseEnter={e => { e.currentTarget.style.background = "#EF4444"; e.currentTarget.style.color = "#fff"; }} onMouseLeave={e => { e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)"; e.currentTarget.style.color = "#EF4444"; }}>
                      <XCircle size={15} /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail */}
        {selected && (
          <div className="card" style={{ padding: 28, position: "sticky", top: 24, background: "#fff", border: "1px solid var(--border)", boxShadow: "var(--shadow-md)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid var(--border)" }}>
              <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 18 }}>Submission Details</h3>
              <button onClick={() => setSelected(null)} style={{ border: "none", background: "var(--surface)", width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 18, color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.2s" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#FEE2E2"; e.currentTarget.style.color = "#EF4444"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.color = "var(--text-muted)"; }}>×</button>
            </div>

            {/* Images */}
            {selected.images?.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ aspectRatio: "1", background: "var(--surface)", borderRadius: 16, overflow: "hidden", marginBottom: 10, border: "1px solid var(--border)" }}>
                  <img src={selected.images[activeImg]} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
                {selected.images.length > 1 && (
                  <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                    {selected.images.map((img, i) => (
                      <button key={i} onClick={() => setActiveImg(i)}
                        style={{ width: 48, height: 48, flexShrink: 0, border: `2px solid ${i === activeImg ? "var(--brand)" : "transparent"}`, borderRadius: 8, overflow: "hidden", padding: 0, background: "#fff", cursor: "pointer", opacity: i === activeImg ? 1 : 0.6, transition: "all 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.opacity = 1}
                        onMouseLeave={e => { if (i !== activeImg) e.currentTarget.style.opacity = 0.6; }}>
                        <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <h4 style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 20, marginBottom: 8, color: "var(--text)", lineHeight: 1.3 }}>{selected.name}</h4>
            <div style={{ fontSize: 24, fontWeight: 800, color: "var(--brand)", fontFamily: "var(--font-head)", marginBottom: 16 }}>{formatPrice(selected.price)}</div>

            <div style={{ background: "#F8FAFC", borderRadius: 12, padding: 16, marginBottom: 20, display: "flex", flexDirection: "column", gap: 10, fontSize: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "var(--text-muted)", fontWeight: 600 }}>Category</span> <span style={{ fontWeight: 600 }}>{selected.category}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "var(--text-muted)", fontWeight: 600 }}>Brand</span> <span style={{ fontWeight: 600 }}>{selected.brand || "—"}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "var(--text-muted)", fontWeight: 600 }}>Stock</span> <span style={{ fontWeight: 600 }}>{selected.stock}</span></div>
              <div style={{ borderTop: "1px solid var(--border)", margin: "4px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "var(--text-muted)", fontWeight: 600 }}>Seller</span> <span style={{ fontWeight: 600 }}>{selected.businessName}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: "var(--text-muted)", fontWeight: 600 }}>Contact</span> <span>{selected.businessEmail}</span></div>
            </div>

            <p style={{ fontSize: 14, lineHeight: 1.7, color: "var(--text-muted)", marginBottom: 24, whiteSpace: "pre-wrap" }}>{selected.description}</p>

            {selected.specs && Object.keys(selected.specs).length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <p style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12, color: "var(--text-muted)" }}>Specifications</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {Object.entries(selected.specs).map(([k, v]) => (
                    <div key={k} style={{ display: "flex", fontSize: 14 }}>
                      <span style={{ width: 140, color: "var(--text-muted)", fontWeight: 600, flexShrink: 0 }}>{k}</span>
                      <span style={{ fontWeight: 500, color: "var(--text)" }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16, paddingTop: 20, borderTop: "1px solid var(--border)" }}>
              <button onClick={() => approve(selected)} className="btn btn-lg" style={{ background: "#10B981", color: "#fff", border: "none", fontSize: 14, padding: "12px 16px" }}>
                <CheckCircle size={18} /> Approve
              </button>
              <button onClick={() => reject(selected.id)} className="btn btn-lg" style={{ background: "#EF4444", color: "#fff", border: "none", fontSize: 14, padding: "12px 16px" }}>
                <XCircle size={18} /> Reject
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
